import { type FormEvent, useState } from 'react'
import { useForm, Link } from '@inertiajs/react'
import { ArrowLeft, Eye } from 'lucide-react'
import RichTextEditor from '@/components/RichTextEditor'
import ImageUpload from '@/components/ImageUpload'
import { TagCombobox } from '@/components/TagCombobox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type BlogPostFormData = {
  title: string
  slug: string
  excerpt: string
  body: string
  byline: string
  blog_category_id: string | null
  tags: string[]
  featured: boolean
  comments_enabled: boolean
  published_at: string
  meta_title: string
  meta_description: string
  cover_image: string | null
  cover_image_preview_url: string | null
  remove_cover_image: boolean
  og_image: string | null
  og_image_preview_url: string | null
  remove_og_image: boolean
}

export type BlogPostFormProps = {
  initialData: BlogPostFormData
  status: 'draft' | 'published'
  categories: { id: string; name: string; slug: string }[]
  postId?: string
  errors?: Partial<Record<string, string[] | string>>
}

const NONE_SENTINEL = '__none__'
const TAG_SUGGESTIONS: string[] = []

const STATUS_LABELS: Record<string, { label: string; tone: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Draft', tone: 'outline' },
  published: { label: 'Published', tone: 'default' },
}

export default function BlogPostForm({
  initialData,
  status,
  categories,
  postId,
  errors,
}: BlogPostFormProps) {
  const form = useForm<BlogPostFormData>(initialData)
  const { data, setData, processing, transform } = form
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const statusInfo = STATUS_LABELS[status]

  function wrapTransform() {
    transform(({
      cover_image_preview_url: _a,
      og_image_preview_url: _b,
      ...rest
    }) => ({ blog_post: rest }))
  }

  function saveDraft(e: FormEvent) {
    e.preventDefault()
    wrapTransform()
    setBusyAction('save')
    if (postId) {
      form.patch(`/admin/blog_posts/${postId}`, { onFinish: () => setBusyAction(null) })
    } else {
      form.post('/admin/blog_posts', { onFinish: () => setBusyAction(null) })
    }
  }

  function transitionTo(path: string, action: string) {
    wrapTransform()
    setBusyAction(action)
    form.post(`/admin/blog_posts/${postId}/${path}`, { onFinish: () => setBusyAction(null) })
  }

  const errorFor = (field: keyof BlogPostFormData | string) => {
    const e = errors?.[field as string]
    if (!e) return null
    return Array.isArray(e) ? e.join(', ') : String(e)
  }

  return (
    <form onSubmit={saveDraft} className="space-y-6 pb-24">
      <header className="flex items-center justify-between gap-4">
        <div>
          <Link
            href="/admin/blog_posts"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Back to posts
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <h1 className="font-serif text-3xl font-semibold tracking-tight">
              {postId ? 'Edit post' : 'New post'}
            </h1>
            <Badge variant={statusInfo.tone}>{statusInfo.label}</Badge>
          </div>
        </div>
        {postId && (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a
                href={`/admin/blog_posts/${postId}/preview`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Eye className="size-4" />
                Preview
              </a>
            </Button>
            {data.slug && status === 'published' && (
              <Button asChild variant="outline" size="sm">
                <a href={`/blog/${data.slug}`} target="_blank" rel="noopener noreferrer">
                  View live
                </a>
              </Button>
            )}
          </div>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Article</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title<span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="title"
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  className="text-lg"
                  placeholder="A compelling headline"
                />
                {errorFor('title') && <p className="text-xs text-destructive">{errorFor('title')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt">
                  Excerpt<span className="text-destructive"> *</span>
                </Label>
                <Textarea
                  id="excerpt"
                  value={data.excerpt}
                  onChange={(e) => setData('excerpt', e.target.value)}
                  rows={2}
                  maxLength={280}
                  placeholder="One or two sentences shown on listings and used as the default meta description."
                />
                <p className="text-xs text-muted-foreground">{data.excerpt.length} / 280</p>
                {errorFor('excerpt') && <p className="text-xs text-destructive">{errorFor('excerpt')}</p>}
              </div>

              <div className="space-y-2">
                <Label>
                  Body<span className="text-destructive"> *</span>
                </Label>
                <RichTextEditor
                  variant="full"
                  enableImageResize
                  content={data.body}
                  onChange={(html) => setData('body', html)}
                />
                {errorFor('body') && <p className="text-xs text-destructive">{errorFor('body')}</p>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SEO &amp; Sharing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta title</Label>
                <Input
                  id="meta_title"
                  value={data.meta_title}
                  onChange={(e) => setData('meta_title', e.target.value)}
                  maxLength={70}
                  placeholder="Defaults to title if left blank"
                />
                <p className="text-xs text-muted-foreground">{data.meta_title.length} / 70</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta description</Label>
                <Textarea
                  id="meta_description"
                  value={data.meta_description}
                  onChange={(e) => setData('meta_description', e.target.value)}
                  rows={2}
                  maxLength={200}
                  placeholder="Defaults to excerpt if left blank"
                />
                <p className="text-xs text-muted-foreground">{data.meta_description.length} / 200</p>
              </div>

              <div className="space-y-2">
                <Label>OG image (social sharing)</Label>
                <ImageUpload
                  currentUrl={data.og_image_preview_url}
                  onUpload={(signedId) =>
                    setData((d) => ({
                      ...d,
                      og_image: signedId || null,
                      remove_og_image: !signedId,
                    }))
                  }
                  onPreviewChange={(url) => setData('og_image_preview_url', url)}
                />
                <p className="text-xs text-muted-foreground">
                  Falls back to cover image if not set. Recommended 1200×630.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="slug">URL slug</Label>
                <Input
                  id="slug"
                  value={data.slug}
                  onChange={(e) => setData('slug', e.target.value)}
                  placeholder="auto-generated from title"
                />
                <p className="text-xs text-muted-foreground">
                  /blog/<span className="font-mono">{data.slug || 'your-slug'}</span>
                </p>
                {errorFor('slug') && <p className="text-xs text-destructive">{errorFor('slug')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="byline">Byline</Label>
                <Input
                  id="byline"
                  value={data.byline}
                  onChange={(e) => setData('byline', e.target.value)}
                  placeholder="e.g. By Jane Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="published_at">Publish date</Label>
                <Input
                  id="published_at"
                  type="date"
                  value={data.published_at}
                  onChange={(e) => setData('published_at', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Controls the order on /blog (newest first) and the date shown on the post. Leave blank to use the time of publishing.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={data.blog_category_id ?? NONE_SENTINEL}
                  onValueChange={(v) =>
                    setData('blog_category_id', v === NONE_SENTINEL ? null : v)
                  }
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="No category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={NONE_SENTINEL}>No category</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No categories yet.{' '}
                    <Link href="/admin/blog_categories/new" className="text-primary hover:underline">
                      Create one
                    </Link>
                  </p>
                )}
              </div>

              <TagCombobox
                label="Tags"
                placeholder="Add a tag"
                suggestions={TAG_SUGGESTIONS}
                selected={data.tags}
                onChange={(tags) => setData('tags', tags)}
                allowCustom
              />

              <div className="flex items-start justify-between gap-3 rounded-lg bg-muted/30 p-3">
                <div>
                  <Label htmlFor="featured" className="text-sm font-medium">
                    Featured
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Highlights this post at the top of /blog.
                  </p>
                </div>
                <Switch
                  id="featured"
                  checked={data.featured}
                  onCheckedChange={(v) => setData('featured', !!v)}
                />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-lg bg-muted/30 p-3">
                <div>
                  <Label htmlFor="comments_enabled" className="text-sm font-medium">
                    Comments
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When off, the comments section is hidden and new comments are rejected.
                  </p>
                </div>
                <Switch
                  id="comments_enabled"
                  checked={data.comments_enabled}
                  onCheckedChange={(v) => setData('comments_enabled', !!v)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cover image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ImageUpload
                currentUrl={data.cover_image_preview_url}
                onUpload={(signedId) =>
                  setData((d) => ({
                    ...d,
                    cover_image: signedId || null,
                    remove_cover_image: !signedId,
                  }))
                }
                onPreviewChange={(url) => setData('cover_image_preview_url', url)}
              />
              {errorFor('cover_image') && (
                <p className="text-xs text-destructive">{errorFor('cover_image')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-black/5 bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-end gap-2 px-4 py-3 sm:px-6 lg:pl-72 lg:pr-8">
          <Button
            type="submit"
            variant="outline"
            disabled={processing}
          >
            {busyAction === 'save' ? 'Saving…' : status === 'published' ? 'Save changes' : 'Save draft'}
          </Button>
          {postId && status === 'draft' && (
            <Button
              type="button"
              disabled={processing}
              onClick={() => transitionTo('publish', 'publish')}
            >
              {busyAction === 'publish' ? 'Publishing…' : 'Publish'}
            </Button>
          )}
          {postId && status === 'published' && (
            <Button
              type="button"
              variant="destructive"
              disabled={processing}
              onClick={() => transitionTo('unpublish', 'unpublish')}
            >
              {busyAction === 'unpublish' ? 'Unpublishing…' : 'Unpublish'}
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}
